import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import { TranslocoDirective } from '@jsverse/transloco';
import { ThemeService } from 'src/app/core/services/theme/theme.service';
import { ThemePipe } from 'src/app/core/pipes/theme.pipe';
import { CurrencyButtonComponent } from 'src/app/ui/buttons/currency-button/currency-button.component';
import {
  NavButtonComponent,
  navButtonIconSizeCss,
  navButtonSizeCss,
} from 'src/app/ui/buttons/nav-button/nav-button.component';
import { CurrencyAmountComponent } from 'src/app/ui/currency/currency-amount/currency-amount.component';
import { IconComponent } from 'src/app/ui/icon/icon/icon.component';
import { CurrencyAmountDisplay } from 'src/app/shared/utils/currency/currency-format';
import { WaitingForPaymentDrawerComponent } from 'src/app/features/payments/components/waiting-for-payment-drawer/waiting-for-payment-drawer.component';
import { LightningInvoicePanelComponent } from 'src/app/features/payments/components/lightning-invoice-panel/lightning-invoice-panel.component';
import { SoloStatComponent } from '../solo-stat/solo-stat.component';
import type { MultiplierSentence } from '../../utils/multiplier-sentence.utils';
import type { SoloAmountDisplay } from '../../utils/solo-tiers.utils';

// The Solo sheet's look: the target to beat, the stake slider, the prize for the chosen stake, and
// Play or Pay. It renders from plain values and reports taps through outputs; solo-sheet binds
// them from the quote, the tiers and the sheet's payment, and owns everything a tap sets off.
@Component({
  selector: 'app-solo-sheet-view',
  templateUrl: './solo-sheet-view.component.html',
  styleUrls: ['./solo-sheet-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    NgStyle,
    NgTemplateOutlet,
    TranslocoDirective,
    ThemePipe,
    CurrencyButtonComponent,
    NavButtonComponent,
    CurrencyAmountComponent,
    IconComponent,
    SoloStatComponent,
    LightningInvoicePanelComponent,
    WaitingForPaymentDrawerComponent,
  ],
})
export class SoloSheetViewComponent {
  // The target to beat, formatted; empty until a quote or tier gives one.
  readonly target = input.required<string>();
  // The selected stake's entry and prize; undefined while no stake is offered.
  readonly entry = input<SoloAmountDisplay>();
  readonly prize = input<SoloAmountDisplay>();
  // The rule line, cut around the multiplier so the multiplier takes its own colour.
  readonly rule = input.required<MultiplierSentence>();
  readonly hasSecondaryCurrency = input(false);

  // The slider: the selected stake's position, the last stake's, and the track the thumb runs on.
  // A lone stake is pinned at the right end of a 0..1 track (singleStake).
  readonly selectedIndex = input.required<number>();
  readonly lastIndex = input.required<number>();
  readonly singleStake = input(false);
  readonly sliderMax = input.required<number>();
  readonly sliderValue = input.required<number>();
  readonly progressPercent = input.required<number>();
  // One label per stake, at `position` percent of the thumb's travel.
  readonly stops = input.required<{ amount: CurrencyAmountDisplay; position: number }[]>();

  // The held quote's countdown, or quoteFailed when no quote could be fetched.
  readonly quoteFailed = input(false);
  readonly quotePercent = input(0);
  readonly quoteSeconds = input(0);

  readonly showRules = input(false);
  // True from the operator Play tap while the charge runs: the stake can't change.
  readonly paying = input(false);
  readonly playDisabled = input(false);

  // Lightning only. `lightning` picks Pay over Play. showInvoiceActions is web (the QR or Copy),
  // and showQR desktop web, where the QR sits in the stake panel.
  readonly lightning = input(false);
  readonly showInvoiceActions = input(false);
  readonly showQR = input(false);
  // The selected stake's invoice, undefined until it is minted.
  readonly selectedInvoice = input<string>();
  readonly invoicePending = input(false);
  readonly payDisabled = input(false);
  // True once a native or operator wallet has the invoice: the sheet shows the waiting state.
  readonly waiting = input(false);
  readonly invoiceCancelled = input(false);

  readonly closeClickedEvent = output<void>();
  readonly rulesClickedEvent = output<void>();
  readonly stepClickedEvent = output<-1 | 1>();
  // The slider's new stake index.
  readonly stakeSelectedEvent = output<number>();
  readonly copyClickedEvent = output<void>();
  readonly payClickedEvent = output<void>();
  readonly playClickedEvent = output<void>();

  private readonly themeService = inject(ThemeService);

  protected readonly qrInPanel = computed(() => this.showInvoiceActions() && this.showQR());
  // Fixed rather than the pay drawer's viewport-scaled width: the QR shares the panel's row with
  // Copy, and the panel is as wide on every desktop.
  protected readonly panelQrWidth = 144;

  protected readonly drawerConfig = this.themeService.payDrawerConfig();
  // Must match the wager drawer's glow choice, so the sheet opened from it keeps its shape.
  protected readonly glowClass =
    this.drawerConfig.gradient === 'circle' ? 'glow-circle' : 'glow-linear';
  // The corner opposite the close button, which the skin places.
  protected readonly rulesButtonPosition =
    this.drawerConfig.closeButtonPosition === 'absolute-top-left-drawer'
      ? 'absolute-top-right-drawer'
      : 'absolute-top-left-drawer';
  // The close button's box and icon sizes, so the Rules link can take the same box.
  protected readonly closeButtonSizeCss = navButtonSizeCss(
    this.drawerConfig.closeButtonInverted ? 'secondary' : 'primary',
    this.drawerConfig.closeButtonSize,
  );
  protected readonly closeButtonIconSizeCss = `var(${navButtonIconSizeCss(this.drawerConfig.closeButtonSize)})`;

  readonly drawerStyles: [string, string][] = [
    ['background', 'drawer'],
    ['border-radius-top', 'drawer'],
  ];
  readonly targetLabelStyles: [string, string][] = [
    ['type', 'drawer-solo-label-heading'],
    ['text-color', 'drawer-solo-label-heading'],
  ];
  // The figures in the Target, Win and Entry boxes, set as the card's are; "pts" takes the box
  // label's type and ink.
  readonly statValueStyles: [string, string][] = [
    ['type', 'drawer-solo-stat-value'],
    ['text-outer-border', 'drawer-solo-stat-value'],
    ['text-color', 'drawer-solo-stat-value'],
  ];
  readonly statPrizeStyles: [string, string][] = [
    ['type', 'drawer-solo-stat-prize'],
    ['text-outer-border', 'drawer-solo-stat-prize'],
    ['text-color', 'drawer-solo-stat-prize'],
  ];
  readonly statUnitStyles: [string, string][] = [
    ['type', 'drawer-solo-stat-label'],
    ['text-color', 'drawer-solo-stat-label'],
  ];
  readonly rulesStyles: [string, string][] = [
    ['type', 'drawer-solo-rules'],
    ['text-color', 'drawer-solo-rules'],
  ];
  readonly ruleStyles: [string, string][] = [
    ['type', 'drawer-solo-body'],
    ['text-color', 'drawer-solo-body'],
  ];
  readonly ruleMultiplierStyles: [string, string][] = [
    ['text-color', 'drawer-solo-rule-multiplier'],
  ];
  readonly quoteNoteStyles: [string, string][] = [
    ['type', 'drawer-solo-quote-note'],
    ['text-color', 'drawer-solo-quote-note'],
  ];
  readonly quoteSecondsStyles: [string, string][] = [
    ['type', 'drawer-solo-quote-seconds'],
    ['text-color', 'drawer-solo-quote-seconds'],
  ];
  readonly entryPanelStyles: [string, string][] = [
    ['background', 'drawer-solo-entry-panel'],
    ['border-radius', 'drawer-solo-entry-panel'],
  ];
  readonly entryLabelStyles: [string, string][] = [
    ['type', 'drawer-solo-entry-label'],
    ['text-color', 'drawer-solo-entry-label'],
  ];
  // The waiting state's recap: words in the unselected stops' ink, figures in the selected stop's,
  // and the prize in its own ink, so it reads as the bet just placed.
  readonly recapStyles: [string, string][] = [
    ['type', 'drawer-solo-entry-label'],
    ['text-color', 'drawer-solo-stop-label'],
  ];
  readonly recapValueStyles: [string, string][] = [
    ['type', 'drawer-solo-recap-value'],
    ['text-color', 'drawer-solo-stop-selected-label'],
  ];
  readonly recapPrizeStyles: [string, string][] = [
    ['type', 'drawer-solo-recap-value'],
    ['text-color', 'drawer-solo-stat-prize'],
  ];
  readonly stepperStyles: [string, string][] = [
    ['background', 'drawer-solo-stepper'],
    ['border-radius', 'drawer-solo-stepper'],
    ['type', 'drawer-solo-stepper-label'],
    ['text-color', 'drawer-solo-stepper-label'],
  ];
  readonly stopStyles: [string, string][] = [
    ['type', 'drawer-solo-stop-label'],
    ['text-color', 'drawer-solo-stop-label'],
  ];
  readonly selectedStopStyles: [string, string][] = [
    ['type', 'drawer-solo-stop-label'],
    ['text-color', 'drawer-solo-stop-selected-label'],
  ];

  onSliderInput(event: Event): void {
    this.stakeSelectedEvent.emit(Number((event.target as HTMLInputElement).value));
  }

  onStep(delta: -1 | 1): void {
    this.stepClickedEvent.emit(delta);
  }

  onCloseClick(): void {
    this.closeClickedEvent.emit();
  }

  onRulesClick(): void {
    this.rulesClickedEvent.emit();
  }

  onCopyClick(): void {
    this.copyClickedEvent.emit();
  }

  onPayClick(): void {
    this.payClickedEvent.emit();
  }

  onPlayClick(): void {
    this.playClickedEvent.emit();
  }
}
